import { SelectionModel } from "@angular/cdk/collections";
import { catchError, finalize, tap, throwError } from "rxjs";
import { FileModel, FilesApi } from "src/app/hickory-api";
import { AccessControl } from "../shared/authentication/access-control";
import { AuthService } from "../shared/authentication/auth.service";
import { BaseDataSource, TableColumn, TableColumnRenders } from "../shared/data-table/datasource/datasource";
import { fixDate } from "../shared/pipes/fix-date/fix-date.pipe";
import { FilesViewContext } from "./file-view-context";

export interface FilesSearchAndSort {
  searchTerm?: string;
  sortField?: keyof FileModel;
  sortDirection?: 'asc' | 'desc';
  sort?: string;
}

export interface FilesListRequest extends FilesSearchAndSort {
  tenantId: number;
  filterType?: 'image' | 'audio';
  includeAudioUrls?: boolean;
}

export class FilesDataSource extends BaseDataSource<FileModel, FilesApi, FilesListRequest> {

  constructor(
    api: FilesApi,
    private readonly viewContext: FilesViewContext,
    private readonly auth: AuthService
  ) {
    super(api, null, null, null, false);
    this.selection = new SelectionModel<FileModel>(this.viewContext.allowMultiSelect(), []);
  }


  protected columns: TableColumn<FileModel>[];
  public selection: SelectionModel<FileModel>;

  public readonly sortableFields = [
    'FileName',
    'DateCreated',
    'ContentType',
    'FileSize'
  ];

  private readonly fields = [
    ...this.sortableFields,
    'OwnerId',
  ];

  protected get canViewAdmins() {
    return this.auth.isSuperAdmin() || this.auth.hasPermission(AccessControl.AdminManage);
  }

  get availableColumns(): TableColumn<FileModel>[] {
    const buildColumn = (field: string): TableColumn<FileModel> => {
      return {
        field,
        localizationKey: `Files.Table.${field}`,
        render: (file: FileModel) => {
          if (field === 'DateCreated') {
            return TableColumnRenders.FixDate(file, 'DateCreated');
          }
          return file[field];
        },
        noSort: !this.sortableFields.includes(field),
        locked: field === 'FileName',
        link: (file: FileModel) => this.getLink(field, file),
      }
    };
    return this.fields.map(f => buildColumn(f));
  }

  private getLink(col: string, file: FileModel) {
    if (col !== 'FileName' && col !== 'OwnerId') {
      return null;
    }

    if (col === 'FileName') {
      return { onclick: () => this.viewContext.fileSelected.emit(file) };
    }

    if (col === 'OwnerId' && this.canViewAdmins && file?.OwnerId) {
      return { route: `/admin/users/${file.OwnerId}` };
    }
  }

  get queryArguments(): FilesListRequest {
    return this.currentRequest.value;
  }

  public resetSelection(allowMultiSelect: boolean): void {
    this.selection?.clear();
    this.selection = new SelectionModel<FileModel>(allowMultiSelect, []);
  }

  public loadData(_: boolean): void {
    // no op - use loadDataWithCb instead
  }

  public loadDataWithCb(request: FilesListRequest, cb?: (files: FileModel[]) => void): void {
    this.loadingSubject.next(true);
    this.errorMessage.next(null);
    // eslint-disable-next-line @typescript-eslint/no-unsafe-call
    this.service.getAllFilesV4(request.tenantId, request.includeAudioUrls, request.filterType, request.searchTerm)
      // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
      .pipe(
        catchError((err) => {
          this.hasError.next(true);
          this.errorMessage.next('Alert.Attachments.ErrorLoadingFiles');
          this.dataSubject.next([]);
          return throwError(() => err);
        }),
        finalize(() => {
          this.loadingSubject.next(false);
        }),
        tap((response) => {
          this.itemCount = response.length;
          if (this.paginator) {
            this.paginator.pageSize = response.length;
          }

          if (request.sortField && request.sortDirection) {
            response = this.sortData(response, request.sortField, request.sortDirection);
          }

          this.dataSubject.next(response);
          this.loadingSubject.next(false);
          cb?.(response);
        })
      )
      // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
      .subscribe();
  }

  sortData(data: FileModel[], sortField: keyof FileModel, sortDirection: 'asc' | 'desc'): FileModel[] {

    if (!sortField || !sortDirection) {
      return data;
    }

    if (this.sort) {
      this.sort.active = sortField;
      this.sort.direction = sortDirection;
    }

    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    let sortAsc = (a: FileModel, b: FileModel) => 0;
    let sortDesc = sortAsc;
    const getSize = (f: FileModel) => {
      let size = 0;
      let unit = 'B';
      if (f.FileSize) {
        const match = f.FileSize.match(/^([\d.]+)\s*([a-zA-Z]+)$/);
        if (match && !isNaN(parseFloat(match[1]))) {
          size = parseFloat(match[1]);
          unit = match[2].toUpperCase();
        }
      }
      switch (unit) {
        case 'TB': return size * Math.pow(1024, 4);
        case 'GB': return size * Math.pow(1024, 3);
        case 'MB': return size * Math.pow(1024, 2);
        case 'KB': return size * 1024;
        default: return size;
      }
    };

    const getTime = (f: FileModel) => fixDate(f.DateCreated).getTime() || 0

    switch (sortField) {
      case 'FileName':
      case 'ContentType':
        sortAsc = (a, b) => a[sortField].localeCompare(b[sortField]);
        sortDesc = (a, b) => b[sortField].localeCompare(a[sortField]);
        break;
      case 'FileSize':
      case 'Length':
        sortAsc = (a, b) => getSize(a) - getSize(b);
        sortDesc = (a, b) => getSize(b) - getSize(a);
        break;
      case 'DateCreated':
        sortAsc = (a, b) => getTime(a) - getTime(b);
        sortDesc = (a, b) => getTime(b) - getTime(a);
        break;
    }
    if (sortDirection === 'asc') {
      return data.sort(sortAsc);
    }
    return data.sort(sortDesc);
  }

  sortWithoutLoad(sortField: keyof FileModel, sortDirection: 'asc' | 'desc'): void {
    this.dataSubject.next(this.sortData(this.dataSubject.value, sortField, sortDirection));
  }
}